import { Banner } from '@mm/components';
import { useResolutionInfo } from '@mm/hooks';
import { InfoCircleSolidIcon } from '@mm/icons';
import {
  FBShareLinksSlideoutBanner,
  IGShareLinksSlideoutBanner,
} from '@mm/icons/SlideoutBanners';
import { useChatStartersCRUDActions } from '@mm/state/actions/ChatStartersCRUDActinos';
import { useIceBreakerCRUDActions } from '@mm/state/actions/IceBreakerCRUDActions';
import { usePromoterCRUDActions } from '@mm/state/actions/PromoterCRUDActions';
import { useModalActions } from '@mm/state/modules/modalsState/actions';
import { selectIGIceBreaker } from '@mm/state/selectors/iceBreakerSelectors';
import { pageEntityByIdSelector } from '@mm/state/selectors/pageSelectors';
import { selectPromoterById } from '@mm/state/selectors/promoterSelector';
import {
  activeChatbotIdSelector,
  chatbotIsActiveOnConnectedPageSelector,
} from '@mm/state/selectors/viewSelectors';
import classNames from 'classnames';
import React, { useEffect } from 'react';
import { useSelector } from 'react-redux';
import { useSupportedWidgets } from '../../../../../common/components/AddWidgetInsert/AddWidgetInsert';
import CollapsibleContentContainer from '../../../../../common/components/CollapsibleContentContainer/CollapsibleContentContainer';
import { DialoguePreview } from '../../../../../modules/DailoguePreview/DialoguePreview';
import { useShowInactiveBannerFlag } from '../../../../common/hooks/use-show-inactive-bot-banner-flag';
import { MOBILE_THRESHOLD } from '../../../../constants/screenSize';
import { CodeWrapper } from '../../../../features/promote/EditPromoterPanel/CodeWrapper/CodeWrapper';
import { EditSlug } from '../../../../features/promote/EditPromoterPanel/EditSlug/EditSlug';
import { EmbeddableDialogueEditorWithPreview } from '../../../../features/startBotPromoters/common/StartBotsDialogueEditor/EmbeddableDialogueEditorWithPreview';
import { usePromoterJSCode } from '../../../../features/startBotPromoters/common/use-promoter-js-code';
import { IG_SUPPORTED_WIDGET_TYPES } from '../../../../features/startBotPromoters/instagramStoryMention/InstagramStoryMentionEditor';
import { PromoterHeader } from '../../components/PromoterHeader/PromoterHeader';
import { SlideoutMenu } from '../../components/SlideoutMenu';
import { FreeTrialWarning } from '../../elements/FreeTrialWarning';
import { useSlideoutOnClose } from '../SlideoutEmail/SlideoutEmail';
import styles from './ShareLinksSlideoutEditor.module.css';

export interface ShareLinksSlideoutEditorProps {
  promoterId: number;
  platform: 'facebook' | 'instagram';
}
export const ShareLinksSlideoutEditor = ({
  promoterId,
  platform,
}: ShareLinksSlideoutEditorProps) => {
  const chatbotId = useSelector(activeChatbotIdSelector);

  const promoter = useSelector(state => selectPromoterById(state, promoterId));

  const promoterCRUDActions = usePromoterCRUDActions();

  const chatbotIsActiveOnConnectedPage = useSelector(
    chatbotIsActiveOnConnectedPageSelector,
  );
  const { onClickActivate } = useShowInactiveBannerFlag();

  const { openActivateInactiveBotModal, openGeneralConfirmModal } =
    useModalActions();

  const { retrieveAll } = useChatStartersCRUDActions();
  const iceBreakerCRUDActions = useIceBreakerCRUDActions();

  const page = useSelector(state =>
    pageEntityByIdSelector(state, String(promoter?.pageId)),
  );
  const igIcebreaker = useSelector(selectIGIceBreaker);

  const { screenWidth } = useResolutionInfo();
  const isMobile = screenWidth <= MOBILE_THRESHOLD;

  const promoterJSCode = usePromoterJSCode(promoter, page);

  const { setRef } = useSlideoutOnClose();

  const valueTitle = `Copy Link`;
  const forInstagram = platform === 'instagram';
  const platformName = forInstagram ? 'Instagram ' : 'Facebook ';
  const { allWidgets: allSupportedWidgets } = useSupportedWidgets();
  const widgetsToExclude = allSupportedWidgets
    .filter(w => !IG_SUPPORTED_WIDGET_TYPES.includes(w.type))
    .map(w => w.type);

  useEffect(() => {
    promoterCRUDActions.retrievePromoterById(chatbotId, promoterId);
  }, [promoterId, page?.id]);

  const customActivateAction = async () => {
    if (promoter) {
      const updatePromoter = async () => {
        await promoterCRUDActions.update(chatbotId, {
          id: promoter.id,
          active: !promoter.active,
        });
        retrieveAll({
          botId: chatbotId,
          kind: forInstagram ? 'instagram_link' : 'link',
        });
      };

      if (!chatbotIsActiveOnConnectedPage && !promoter.active) {
        openActivateInactiveBotModal({
          onBotActivateConfirm: () =>
            setTimeout(() => onClickActivate(updatePromoter), 500),
          useCustomOnBotActivateConfirm: true,
        });
      } else if (!igIcebreaker?.active && !promoter.active && forInstagram) {
        openGeneralConfirmModal({
          headerText: 'Activate Instagram Icebreaker',
          messageText:
            'In order to activate Instagram share links, you have to turn on icebreakers.',
          onCloseHandler: async ok => {
            if (!igIcebreaker) {
              await iceBreakerCRUDActions.retrieveAll(chatbotId, 'instagram');
            }
            if (ok && igIcebreaker) {
              await iceBreakerCRUDActions.update(igIcebreaker.botId, {
                id: igIcebreaker.id,
                active: true,
              });
              updatePromoter();
            }
          },
          okButtonText: 'Turn on',
          okButtonStyling: { blue: true },
          cancelButtonText: 'Cancel',
        });
      } else {
        updatePromoter();
      }
    }
  };

  let orderNumber = 0;

  const nextOrderNumber = () => {
    orderNumber += 1;
    return orderNumber.toString();
  };

  const updateSlug = async (slug: string) => {
    if (promoter) {
      await promoterCRUDActions.update(chatbotId, {
        id: promoter.id,
        slug,
      });
      retrieveAll({
        botId: chatbotId,
        kind: 'link',
      });
    }
  };

  const getBannerProp = () => {
    if (forInstagram) {
      return {
        component: <IGShareLinksSlideoutBanner textColor="white" />,
        css: styles.instagramBanner,
      };
    }
    return {
      component: <FBShareLinksSlideoutBanner textColor="white" />,
      css: styles.facebookBanner,
    };
  };

  if (promoter && page) {
    return (
      <SlideoutMenu className={styles.menu} setRef={setRef}>
        <PromoterHeader
          promoter={promoter}
          customActivateAction={customActivateAction}
          title={`${platformName} Share Link`}
          hideDeleteAction
        />
        <FreeTrialWarning />

        <SlideoutMenu.Content
          className={classNames(
            styles.content,
            forInstagram ? styles.contentInstagram : styles.contentFacebook,
          )}
        >
          {!isMobile && (
            <Banner
              css={{
                container: getBannerProp().css,
                header: getBannerProp().css,
                collapsedHeader: styles.collapsedHeader,
                content: styles.bannerContent,
              }}
              withBannerHeader
              headerText={`What are ${platformName} Share Links?`}
              withLeftArea={false}
              showCloseIcon
              usedInSlideout
            >
              {getBannerProp().component}
            </Banner>
          )}
          <div className={styles.container}>
            <div className={styles.configArea}>
              <CollapsibleContentContainer
                orderNumber={nextOrderNumber()}
                containerName="Reply instantly to people who click your link"
                className={styles.collapsibleContainer}
                stepperStylingPlatform={forInstagram ? 'instagram' : 'facebook'}
                useCircleNumberStyle
                headerClassName={styles.collapsibleContainerHeader}
                childrenContainerClassName={styles.collapsibleChildrenContainer}
              >
                <EmbeddableDialogueEditorWithPreview
                  editPaneClassname={styles.dialogueEditPane}
                  previewClassName={styles.dialogueEditPreviewPane}
                  dialogueId={page.id}
                  widgetsToExclude={forInstagram ? widgetsToExclude : []}
                  platforms={forInstagram ? ['instagram'] : ['facebook']}
                  usedInSlideout
                  hideDialogueSelector
                  showAttributesStartWarning={forInstagram}
                  forInstagram={forInstagram}
                />
              </CollapsibleContentContainer>

              <CollapsibleContentContainer
                orderNumber={nextOrderNumber()}
                containerName="Customize your Share Link"
                className={styles.collapsibleContainer}
                useCircleNumberStyle
                stepperStylingPlatform={forInstagram ? 'instagram' : 'facebook'}
                headerClassName={styles.collapsibleContainerHeader}
                childrenContainerClassName={
                  styles.collapsibleChildrenContainerSlug
                }
              >
                <EditSlug
                  slug={promoter.slug}
                  updateSlug={updateSlug}
                  className={styles.inputWrapper}
                />

                <CodeWrapper
                  code={promoterJSCode}
                  label={valueTitle}
                  className={styles.inputWrapper}
                />

                <div className={styles.messageInfoBlock}>
                  <InfoCircleSolidIcon />
                  {!forInstagram && (
                    <div>
                      {`Share Links will open up in Messenger
                     `}
                      <a
                        className={styles.messageInfoBlocklink}
                        target="_blank"
                        rel="noopener noreferrer"
                        href={promoterJSCode}
                      >
                        Click here to preview your share link.
                      </a>
                    </div>
                  )}
                  {forInstagram && (
                    <div>
                      Link can only be used on mobile device to open thread.
                    </div>
                  )}
                </div>
              </CollapsibleContentContainer>
            </div>
            {!isMobile && (
              <DialoguePreview
                dialogueId={page.id}
                className={styles.dialoguePreview}
                sticky
              />
            )}
          </div>
        </SlideoutMenu.Content>
      </SlideoutMenu>
    );
  }
  return null;
};
